// src/shared/components/DialogProvider.tsx

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';

/**
 * App-weiter Dialog-Provider als Ersatz für window.confirm() und alert().
 *
 * Hintergrund: Chrome unterdrückt window.confirm() und alert(), wenn der Dialog
 * nicht unmittelbar aus einer Nutzergeste heraus entsteht oder das Fenster nicht
 * im Vordergrund liegt ("dialog was suppressed because this page is not the
 * active tab of the front window"). confirm() liefert dann still false zurück,
 * eine Aktion bricht unbemerkt ab, und eine alert()-Meldung geht verloren.
 * Genau das ist beim Bulk-Versand der Rechnungen passiert: Die Vor-Prüfung läuft
 * mehrere Sekunden, der Dialog kommt weit nach dem Klick und wird verworfen.
 *
 * Lösung: Dialoge im DOM statt im Browser. Diese kann Chrome nicht unterdrücken,
 * sie sind unabhängig vom Fensterfokus und beliebig gestaltbar.
 *
 * Verwendung in einer Komponente:
 *
 *   const { confirm, notify } = useDialog();
 *   const ok = await confirm({ titel: '...', text: '...' });
 *   await notify({ titel: '...', text: '...', variante: 'gefahr' });
 *
 * Der Aufruf ist damit ein direkter Ersatz für die Browser-Dialoge: Die
 * umgebende Ablauflogik behält ihre Struktur, es kommt nur ein await davor.
 *
 * Mehrere Aufrufe hintereinander sind erlaubt. Die Dialoge werden in einer
 * Warteschlange gehalten und einzeln nacheinander angezeigt, damit sich zwei
 * Meldungen nie gegenseitig überschreiben.
 */

export type DialogVariante = 'info' | 'warnung' | 'gefahr';

export interface ConfirmOptionen {
  titel: string;
  text: string;
  bestaetigenLabel?: string;
  abbrechenLabel?: string;
  variante?: DialogVariante;
}

export interface NotifyOptionen {
  titel: string;
  text: string;
  schliessenLabel?: string;
  variante?: DialogVariante;
}

export interface DialogApi {
  /** Rückfrage mit Bestätigen/Abbrechen. Liefert true bei Bestätigung. */
  confirm: (optionen: ConfirmOptionen) => Promise<boolean>;
  /** Reine Meldung mit einem Schließen-Knopf. */
  notify: (optionen: NotifyOptionen) => Promise<void>;
}

interface DialogEintrag {
  id: number;
  art: 'confirm' | 'notify';
  titel: string;
  text: string;
  bestaetigenLabel: string;
  abbrechenLabel: string | null;
  variante: DialogVariante;
  aufloesen: (bestaetigt: boolean) => void;
}

const VARIANTEN: Record<DialogVariante, { akzent: string; hintergrund: string; rahmen: string }> = {
  info: { akzent: '#3B82F6', hintergrund: '#EFF6FF', rahmen: '#BFDBFE' },
  warnung: { akzent: '#D97706', hintergrund: '#FFFBEB', rahmen: '#FDE68A' },
  gefahr: { akzent: '#DC2626', hintergrund: '#FEF2F2', rahmen: '#FECACA' }
};

const DialogContext = createContext<DialogApi | null>(null);

/**
 * Zugriff auf die Dialog-Funktionen. Muss innerhalb des DialogProvider
 * aufgerufen werden (dieser hängt in App.tsx ganz außen).
 */
export const useDialog = (): DialogApi => {
  const api = useContext(DialogContext);
  if (!api) {
    throw new Error('useDialog() muss innerhalb von <DialogProvider> verwendet werden.');
  }
  return api;
};

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [warteschlange, setWarteschlange] = useState<DialogEintrag[]>([]);
  const naechsteId = useRef(0);
  const bestaetigenRef = useRef<HTMLButtonElement>(null);

  const anhaengen = useCallback((eintrag: Omit<DialogEintrag, 'id'>) => {
    naechsteId.current += 1;
    const id = naechsteId.current;
    setWarteschlange(vorher => vorher.concat([{ ...eintrag, id }]));
  }, []);

  const confirm = useCallback((optionen: ConfirmOptionen): Promise<boolean> => {
    return new Promise<boolean>(aufloesen => {
      anhaengen({
        art: 'confirm',
        titel: optionen.titel,
        text: optionen.text,
        bestaetigenLabel: optionen.bestaetigenLabel || 'Bestätigen',
        abbrechenLabel: optionen.abbrechenLabel || 'Abbrechen',
        variante: optionen.variante || 'info',
        aufloesen
      });
    });
  }, [anhaengen]);

  const notify = useCallback((optionen: NotifyOptionen): Promise<void> => {
    return new Promise<void>(fertig => {
      anhaengen({
        art: 'notify',
        titel: optionen.titel,
        text: optionen.text,
        bestaetigenLabel: optionen.schliessenLabel || 'OK',
        abbrechenLabel: null,
        variante: optionen.variante || 'info',
        aufloesen: () => fertig()
      });
    });
  }, [anhaengen]);

  const api = useMemo<DialogApi>(() => ({ confirm, notify }), [confirm, notify]);

  const aktiv = warteschlange.length > 0 ? warteschlange[0] : null;

  // Antwort zurückgeben und den Eintrag aus der Warteschlange nehmen. Das
  // Auflösen des Promise passiert bewusst außerhalb des State-Updaters, damit
  // es unter React.StrictMode nicht doppelt ausgeführt wird.
  const beantworten = useCallback((bestaetigt: boolean) => {
    if (!aktiv) return;
    aktiv.aufloesen(bestaetigt);
    setWarteschlange(vorher => vorher.filter(e => e.id !== aktiv.id));
  }, [aktiv]);

  // Tastatursteuerung: Enter bestätigt, Escape bricht ab bzw. schließt.
  useEffect(() => {
    if (!aktiv) return;
    const beiTaste = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        beantworten(false);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        beantworten(true);
      }
    };
    document.addEventListener('keydown', beiTaste);
    return () => document.removeEventListener('keydown', beiTaste);
  }, [aktiv, beantworten]);

  // Fokus auf den bestätigenden Knopf, damit Enter direkt greift.
  useEffect(() => {
    if (aktiv) bestaetigenRef.current?.focus();
  }, [aktiv]);

  const farben = aktiv ? VARIANTEN[aktiv.variante] : VARIANTEN.info;

  return (
    <DialogContext.Provider value={api}>
      {children}
      {aktiv && (
        <div
          onClick={() => beantworten(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000, padding: '20px'
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: '12px', padding: '24px',
              maxWidth: '640px', width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              fontFamily: 'inherit'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                flexShrink: 0, width: '32px', height: '32px', borderRadius: '8px',
                backgroundColor: farben.hintergrund, border: `1px solid ${farben.rahmen}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {aktiv.variante === 'info'
                  ? <Info size={17} color={farben.akzent} />
                  : <AlertTriangle size={17} color={farben.akzent} />}
              </div>
              <h2 style={{
                margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827',
                lineHeight: '32px'
              }}>
                {aktiv.titel}
              </h2>
            </div>

            <div style={{
              fontSize: '14px', color: '#374151', lineHeight: '1.6',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: '50vh', overflowY: 'auto',
              marginBottom: '20px'
            }}>
              {aktiv.text}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {aktiv.abbrechenLabel && (
                <button
                  type="button"
                  onClick={() => beantworten(false)}
                  style={{
                    padding: '8px 16px', backgroundColor: '#F3F4F6', color: '#374151',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: '500'
                  }}
                >
                  {aktiv.abbrechenLabel}
                </button>
              )}
              <button
                ref={bestaetigenRef}
                type="button"
                onClick={() => beantworten(true)}
                style={{
                  padding: '8px 16px', backgroundColor: farben.akzent, color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: '500'
                }}
              >
                {aktiv.bestaetigenLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
};
